import { TEAM_MANAGED_BOARD } from '@atlassian/jira-capabilities/src/constants.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@atlassian/jira-issue-preload-layout/src/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { forgeBoardModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import AsyncSidebarNextGen from '@atlassian/jira-navigation-apps-sidebar-next-gen/src/async.tsx';
import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { boardFilterRefinementResource } from '@atlassian/jira-router-resources-board-resource/src/index.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { softwareBoardRoute } from '@atlassian/jira-router-routes-software-board-routes/src/softwareBoardRoute.tsx';
import { NextGenBoardSkeleton } from '@atlassian/jira-skeletons/src/ui/next-gen-board/NextGenBoardSkeleton.tsx';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { aiEntitlementResource } from '@atlassian/jira-router-resources-ai-entitlement/src/services/index.tsx';
import { softwareProjectLayout } from '@atlassian/jira-software-project-layout/src/index.tsx';
import { projectThemeResource } from '@atlassian/jira-router-resources-project-theme/src/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { coloredLabelsResource } from '@atlassian/jira-router-resources-colored-labels/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { postOfficeJiraIssueViewTwcSpotlightTourResource } from '@atlassian/jira-router-resources-post-office/src/services/index.tsx';
import { conditionalBoardNBTOptInResource } from './common/conditionalBoardNBTOptInResource.tsx';
import { conditionalBoardRelayResource } from './common/conditionalBoardRelayResource.tsx';
import { conditionalBoardResource } from './common/conditionalBoardResource.tsx';
import { conditionalCustomFiltersResource } from './common/conditionalCustomFiltersResource.tsx';
import { softwareBoardPageEntrypoint } from './ui/index.tsx';

const getColoredLabelsResource = () => {
	return expVal(
		'adding-color-to-labels-field-experiment-milestone2',
		'isColorfulNewLabelsEnabled',
		false,
	)
		? [coloredLabelsResource]
		: [];
};

export const softwareBoardRouteEntry = createEntry(softwareBoardRoute, () => ({
	group: 'software',
	skeleton: NextGenBoardSkeleton,

	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				query: ['selectedIssue'],
			},
		]),
	),

	navigation: {
		menuId: MENU_ID.PROJECTS,
		sidebar: AsyncSidebarNextGen,
		horizontal: AsyncHorizontalNavJira,
		onlyShowHorizontalOnNav4: true,
	},

	resources: [
		...getNavigationResources(),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
		projectContextResource,
		staticSidebarShortcutsResource,
		staticProjectSidebarNavigationResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		conditionalBoardResource,
		conditionalCustomFiltersResource,
		boardFilterRefinementResource,
		conditionalBoardRelayResource,
		conditionalBoardNBTOptInResource,
		forgeBoardModuleResource,
		resourceWithCondition2(() => getIsSoftwareThemingEnabled(), projectThemeResource),
		resourceWithCondition2(() => fg('ai_sprint_goal_gate'), aiEntitlementResource),
		...getColoredLabelsResource(),
		resourceWithCondition2(
			() => fg('jira-issue-view-twc-spotlight-tour-ssr'),
			postOfficeJiraIssueViewTwcSpotlightTourResource,
		),
	],

	preloadOptions: {
		earlyChunks: ['software-board-early-entry'],
	},

	afterPaint: [LazyIssueApp],
	entryPoint: () => softwareBoardPageEntrypoint,
	ufoName: 'next-gen-board',

	meta: {
		capability: TEAM_MANAGED_BOARD,
	},

	supportsPreviewPanel: true,
	isHydrateRootEnabled: fg('tmp-board-hydrate-root'),

	bifrost: {
		bifrostGroup: 'shell-only',

		bundling: {
			packageName:
				'@atlassian/jira-router-routes-software-board-entries/src/softwareBoardRouteEntry.tsx',
			whichExportToUse: 'softwareBoardRouteEntry',
		},

		enabled: true,
	},
}));
